import moment from 'moment';
import template from './am-operator-codes.html';
import { CODE_TYPE_ENUM } from '../../core/models/operator.model';

class AmOperatorCodesController {
  constructor(amOperatorsService, usersService) {
    'ngInject';

    this.opService = amOperatorsService;
    this.status = '';
    this.pins = [];
    this.ztr_rfids = [];
    this.ipafs = [];
    this.autoGeneratePin = false;
    this.usersService = usersService;
  }

  $onInit() {}

  $onChanges(changes) {
    const { inCodes } = changes;
    if (inCodes) {
      this.filterCodes();
    }
  }

  $onDestroy() {}

  filterCodes() {
    const codes = Array.isArray(this.inCodes) ? this.inCodes : [];

    this.pins = this.getCodeType(codes, CODE_TYPE_ENUM.PIN);
    this.ztr_rfids = this.getCodeType(codes, CODE_TYPE_ENUM.ZTR_RFID);
    this.ipafs = this.getCodeType(codes, CODE_TYPE_ENUM.IPAF);

    this.updateOutCodes();
  }

  getCodeType(aCodes = [], aType = '') {
    const type = aType.toUpperCase();
    const codes = aCodes.filter((code) => code.type.toUpperCase() === type);

    // Display label for IPAF cards
    if (type === CODE_TYPE_ENUM.IPAF) {
      const labelSet = new Set();
      const ipafLabels = [];
      const ipafCodes = [];

      codes.forEach((code) => {
        if (code.accessCard) {
          const { label } = code.accessCard;

          if (!labelSet.has(label)) {
            labelSet.add(label);
            ipafLabels.push({ type: CODE_TYPE_ENUM.IPAF, identifier: label });
          }
        } else {
          ipafCodes.push({ type: CODE_TYPE_ENUM.IPAF, identifier: code.identifier, error: true });
        }
      });

      return [...ipafLabels, ...ipafCodes];
    }

    return codes;
  }

  addNewCode(identifier, aType) {
    if (identifier < 0) return;

    const type = aType.toUpperCase();

    switch (type) {
      case CODE_TYPE_ENUM.PIN: {
        const newcode = this.createNewCode(CODE_TYPE_ENUM.PIN, identifier);
        this.pins.push(newcode);
        break;
      }
      case CODE_TYPE_ENUM.ZTR_RFID: {
        const newcode = this.createNewCode(CODE_TYPE_ENUM.ZTR_RFID, identifier);
        this.ztr_rfids.push(newcode);
        break;
      }
      case CODE_TYPE_ENUM.IPAF: {
        const newcode = this.createNewCode(CODE_TYPE_ENUM.IPAF, identifier);
        this.ipafs.push(newcode);
        break;
      }
    }

    this.updateOutCodes();
    this.saveCodeChanges(identifier);
  }

  createNewCode(type, identifier) {
    return {
      type,
      identifier,
      loading: true,
    };
  }

  deleteCodeType(code, aType) {
    const type = aType.toUpperCase();

    switch (type) {
      case CODE_TYPE_ENUM.PIN: {
        this.pins = this.deleteCode(this.pins, code);
        break;
      }
      case CODE_TYPE_ENUM.ZTR_RFID: {
        this.ztr_rfids = this.deleteCode(this.ztr_rfids, code);
        break;
      }
      case CODE_TYPE_ENUM.IPAF: {
        this.ipafs = this.deleteCode(this.ipafs, code);
        break;
      }
      default:
        break;
    }

    this.updateOutCodes();
    this.saveCodeChanges();
  }

  deleteCode(codeArray, code) {
    if (!code) return codeArray;
    const indexOfCode = codeArray.findIndex((c) => c.identifier === code.identifier);
    if (indexOfCode > -1) {
      codeArray[indexOfCode].toBeRemoved = true;
      codeArray[indexOfCode].loading = true;
    }
    return codeArray;
  }

  saveCodeChanges(identifier) {
    // show warning popup if user try to save with no codes
    if (!this.autoGeneratePin && this.outCodes.length < 1) {
      this.opService
        .openNoCodeWarningPopup()
        .then(() => {
          this.updateOperatorCodes();
        })
        .catch(() => {
          this.inCodes.forEach((code) => {
            if (code.loading) code.loading = false;
            if (code.toBeRemoved) code.toBeRemoved = false;
          });

          // to reset
          this.filterCodes();
        });
    } else {
      this.updateOperatorCodes(identifier);
    }
  }

  updateOperatorCodes(identifier) {
    this.status = 'updating';
    this.opService
      .updateOperatorsCodes(this.operatorId, this.outCodes, this.autoGeneratePin)
      .then((result) => {
        this.autoGeneratePin = false;
        this.status = 'success';
        this.onSaveSuccess({ codes: result });
        // If user has the correct expiry feature update the operator with the expired code from SQEP
        if (this.usersService.hasRequiredFeatures(['access.operator_expiry'])) {
          if (result.types.includes('IPAF')) {
            this.checkForOperatorIPAFExpiry(result.accessCodes, identifier);
          } else if (this.operatorModel.attrs.isIPAFExpiry) {
            delete this.operatorModel.attrs.isIPAFExpiry;
            delete this.editAttrs.isIPAFExpiry;
            this.opService.updateOperatorMetaData(
              this.operatorId,
              this.operatorModel.name,
              this.operatorModel.attrs,
            );
          }
        }
      })
      .catch((error) => {
        this.errorCode = error.status;
        this.updatedIdentifier = identifier;
        this.status = 'error';
        // to reset
        this.filterCodes();
      });
  }

  updateOutCodes() {
    const updatedPins = this.pins
      .filter((c) => !c.toBeRemoved)
      .map((c) => ({ type: CODE_TYPE_ENUM.PIN, identifier: c.identifier }));

    const updatedZtrRfids = this.ztr_rfids
      .filter((c) => !c.toBeRemoved)
      .map((c) => ({ type: CODE_TYPE_ENUM.ZTR_RFID, identifier: c.identifier }));

    const updatedIpafs = this.ipafs
      .filter((c) => !c.toBeRemoved)
      .map((c) => ({ type: CODE_TYPE_ENUM.IPAF, identifier: c.identifier }));

    this.outCodes = [...updatedPins, ...updatedZtrRfids, ...updatedIpafs];
  }

  generateCodeClick() {
    this.autoGeneratePin = true;
    if (this.operatorId) {
      this.updateOperatorCodes();
    } else {
      this.onSave();
    }
  }

  checkForOperatorIPAFExpiry(accessCodes, identifier) {
    for (let i = 0; i < accessCodes.length; i++) {
      if (accessCodes[i].type === 'IPAF') {
        this.opService
          .getOperatorExpiryById(accessCodes[i].accessCard.label)
          .then((expiry) => {
            const expiryDate = moment(expiry.expires);

            this.operatorModel.attrs.expiry = expiryDate.toISOString();
            this.operatorModel.attrs.isIPAFExpiry = true;

            this.editAttrs.expiry = expiryDate.toISOString();
            this.editAttrs.isIPAFExpiry = true;

            this.opService.updateOperatorMetaData(
              this.operatorId,
              this.operatorModel.name,
              this.operatorModel.attrs,
            );
          })
          .catch((error) => {
            this.errorCode = error.status;
            this.updatedIdentifier = identifier;
            this.status = 'error';
            // to reset
            this.filterCodes();
          });
        break;
      }
    }
  }
}

export default {
  template,
  controller: AmOperatorCodesController,
  controllerAs: 'opCodesVm',
  bindings: {
    operatorId: '<',
    operatorModel: '=',
    editAttrs: '=',
    inCodes: '<',
    outCodes: '=?',
    autoGeneratePin: '=?',
    onSaveSuccess: '&',
    onSave: '&',
  },
};
